const ACCESS_KEY_DB = 'farfar-access-v1';
const ACCESS_KEYS_STORE = 'keys';

export type AccessStoredKeyKind = 'custodian' | 'session';

export type AccessStoredKey = {
  kind: AccessStoredKeyKind;
  publicKeyFingerprint: string;
  publicKeyJwk: JsonWebKey;
  privateKey: CryptoKey;
  role?: string;
  sessionId?: string;
  createdAt: string;
};

export type AccessKeyStore = {
  putKey(record: AccessStoredKey): Promise<void>;
  getKeyByFingerprint(fingerprint: string): Promise<AccessStoredKey | null>;
  getSessionKey(sessionId: string): Promise<AccessStoredKey | null>;
  deleteKeyByFingerprint(fingerprint: string): Promise<void>;
  listKeys(): Promise<AccessStoredKey[]>;
};

export function createMemoryAccessKeyStore(): AccessKeyStore {
  const keys = new Map<string, AccessStoredKey>();
  return {
    async putKey(record) {
      keys.set(record.publicKeyFingerprint, { ...record });
    },
    async getKeyByFingerprint(fingerprint) {
      return keys.get(fingerprint) || null;
    },
    async getSessionKey(sessionId) {
      return [...keys.values()].find((row) => row.sessionId === sessionId) || null;
    },
    async deleteKeyByFingerprint(fingerprint) {
      keys.delete(fingerprint);
    },
    async listKeys() {
      return [...keys.values()];
    }
  };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(ACCESS_KEY_DB, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(ACCESS_KEYS_STORE)) {
        const store = db.createObjectStore(ACCESS_KEYS_STORE, { keyPath: 'publicKeyFingerprint' });
        store.createIndex('sessionId', 'sessionId', { unique: false });
        store.createIndex('kind', 'kind', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new Error('Could not open the court-access key database.'));
  });
}

function requestPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new Error('Court-access key storage failed.'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(new Error('Court-access key storage transaction failed.'));
    transaction.onabort = () => reject(new Error('Court-access key storage transaction aborted.'));
  });
}

export function createIndexedDbAccessKeyStore(): AccessKeyStore {
  return {
    async putKey(record) {
      const db = await openDb();
      try {
        const transaction = db.transaction(ACCESS_KEYS_STORE, 'readwrite');
        transaction.objectStore(ACCESS_KEYS_STORE).put(record);
        await transactionDone(transaction);
      } finally {
        db.close();
      }
    },
    async getKeyByFingerprint(fingerprint) {
      const db = await openDb();
      try {
        return (await requestPromise(
          db.transaction(ACCESS_KEYS_STORE, 'readonly').objectStore(ACCESS_KEYS_STORE).get(fingerprint)
        )) || null;
      } finally {
        db.close();
      }
    },
    async getSessionKey(sessionId) {
      const db = await openDb();
      try {
        return (await requestPromise(
          db.transaction(ACCESS_KEYS_STORE, 'readonly')
            .objectStore(ACCESS_KEYS_STORE)
            .index('sessionId')
            .get(sessionId)
        )) || null;
      } finally {
        db.close();
      }
    },
    async deleteKeyByFingerprint(fingerprint) {
      const db = await openDb();
      try {
        const transaction = db.transaction(ACCESS_KEYS_STORE, 'readwrite');
        transaction.objectStore(ACCESS_KEYS_STORE).delete(fingerprint);
        await transactionDone(transaction);
      } finally {
        db.close();
      }
    },
    async listKeys() {
      const db = await openDb();
      try {
        return (await requestPromise(
          db.transaction(ACCESS_KEYS_STORE, 'readonly').objectStore(ACCESS_KEYS_STORE).getAll()
        )) || [];
      } finally {
        db.close();
      }
    }
  };
}
